import { addMinutes, format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import type { Company, Location, Reservation } from '~/core/types';
import { homologateDay } from '~/utils/date';

/**
 * Arma, a partir de una reserva, lo que hace falta para meterla en el
 * calendario del cliente: los enlaces de Google, Outlook y Yahoo, y el archivo
 * `.ics` para Apple y todo lo demás.
 *
 * Todo es texto que se arma aquí, en el navegador. No hay ninguna API, ninguna
 * clave ni ningún servicio externo: coste cero y nada que pueda caerse.
 *
 * Tres decisiones que conviene conocer:
 *
 *  · **Las horas van en UTC.** La reserva guarda `startDatetime`/`endDatetime`
 *    como instantes UTC; se pasan tal cual con la `Z` al final y cada
 *    calendario los pinta en la zona del cliente. Es la única forma que no
 *    falla cuando la persona está de viaje.
 *
 *  · **El `UID` es el id de la reserva y `SEQUENCE` crece con cada cambio.**
 *    Así, si el cliente modifica la reserva y vuelve a añadirla, su calendario
 *    ACTUALIZA el evento en vez de duplicarlo. Los enlaces de Google y Outlook
 *    no saben hacer eso: siempre crean uno nuevo.
 *
 *  · **La dirección sale de la configuración de la empresa**, nunca de
 *    `location.address`: en NOI ese campo guarda aforos ("6 PERSONAS"), no
 *    direcciones.
 */

export type Idioma = 'es' | 'en';

export type EventoCalendario = {
  /** Identificador estable: el id del documento de la reserva. */
  uid: string;
  /** Sube con cada modificación; es lo que hace que se actualice. */
  secuencia: number;
  titulo: string;
  descripcion: string;
  /** Dirección completa. Vacía si la empresa no la ha configurado. */
  lugar: string;
  inicio: Date;
  fin: Date;
  /** Enlace para ver, modificar o cancelar. */
  url: string;
  /** Minutos antes para el aviso. Sin valor, sin aviso. */
  avisoMinutos?: number;
  cancelado: boolean;
};

const ZONA_POR_DEFECTO = 'America/Bogota';
const DURACION_POR_DEFECTO_MIN = 60;

const aFecha = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const conToDate = v as { toDate?: () => Date };
  if (typeof conToDate.toDate === 'function') return conToDate.toDate();
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : new Date(t);
  }
  return null;
};

const aMilisegundos = (v: unknown): number => aFecha(v)?.getTime() ?? 0;

const TEXTOS: Record<Idioma, Record<string, string>> = {
  es: {
    codigo: 'Código de reserva',
    personas: 'Personas',
    espacio: 'Espacio',
    direccion: 'Dirección',
    comoLlegar: 'Cómo llegar',
    gestionar: 'Para ver, modificar o cancelar tu reserva',
    cancelada: 'Esta reserva fue cancelada.',
    reserva: 'Reserva',
  },
  en: {
    codigo: 'Reservation code',
    personas: 'People',
    espacio: 'Space',
    direccion: 'Address',
    comoLlegar: 'Directions',
    gestionar: 'To view, change or cancel your reservation',
    cancelada: 'This reservation was cancelled.',
    reserva: 'Reservation',
  },
};

/**
 * Cuándo acaba una reserva que no trae hora de fin.
 *
 * La Zona coworking guarda hora de llegada pero no de salida (es un pase de
 * día). Lo más fiel es "hasta que cierra ese día"; si no se conoce el horario,
 * el bloque de la sala; y si tampoco, una hora.
 */
const finEstimado = (
  inicio: Date,
  fechaTexto: string | undefined,
  sede: Location | undefined,
  zona: string
): Date => {
  const ajustes = (sede?.schedule?.settings ?? {}) as {
    isReservationWholeDay?: boolean;
    blockTimeMinutes?: number;
  };

  if (ajustes.blockTimeMinutes && ajustes.blockTimeMinutes > 0) {
    return addMinutes(inicio, ajustes.blockTimeMinutes);
  }

  if (ajustes.isReservationWholeDay && fechaTexto) {
    // El horario semanal guarda los días con 0 = lunes, no como `getDay()`
    // (0 = domingo). `homologateDay` es la misma traducción que usa el
    // formulario para ofrecer las horas; sin ella se leería el día siguiente.
    const diaSemana = homologateDay[new Date(`${fechaTexto}T12:00:00`).getDay()];
    const cierre = sede?.schedule?.['weekly-dates']?.[diaSemana]?.closing;
    if (cierre && /^\d{1,2}:\d{2}$/.test(cierre)) {
      const fin = zonedTimeToUtc(`${fechaTexto} ${cierre.padStart(5, '0')}`, zona);
      if (fin > inicio) return fin;
    }
  }

  return addMinutes(inicio, DURACION_POR_DEFECTO_MIN);
};

/** La reserva convertida en un evento de calendario. */
export const eventoDeReserva = ({
  reserva,
  reservaId,
  empresa,
  sede,
  idioma,
  origen,
}: {
  reserva: Reservation;
  reservaId: string;
  empresa: Company;
  /** La sede completa (con horario), si se ha podido cargar. */
  sede?: Location;
  idioma: Idioma;
  /** `window.location.origin` del formulario. */
  origen: string;
}): EventoCalendario | null => {
  const inicio = aFecha(reserva.startDatetime);
  if (!inicio) return null;

  const cfg = empresa.settings?.calendar ?? {};
  const zona = cfg.timezone || ZONA_POR_DEFECTO;
  const t = TEXTOS[idioma] ?? TEXTOS.es;

  const fin = aFecha(reserva.endDatetime) ?? finEstimado(inicio, reserva.date, sede, zona);

  const nombreSede = reserva.location?.name ?? '';
  const titulo = [nombreSede, empresa.name].filter(Boolean).join(' · ');

  const url = `${origen}/form/${empresa.externalId}?code=${encodeURIComponent(
    reserva.code ?? ''
  )}&lang=${idioma}`;

  const cancelado = reserva.status === 'cancelled';
  const lineas = [
    cancelado ? t.cancelada : null,
    `${t.codigo}: ${reserva.code ?? ''}`,
    reserva.numberPeople ? `${t.personas}: ${reserva.numberPeople}` : null,
    nombreSede ? `${t.espacio}: ${nombreSede}` : null,
    cfg.address ? `${t.direccion}: ${cfg.address}` : null,
    cfg.mapsUrl ? `${t.comoLlegar}: ${cfg.mapsUrl}` : null,
    '',
    `${t.gestionar}: ${url}`,
  ].filter((l): l is string => l !== null);

  const creada = aMilisegundos(reserva.createdAt);
  const actualizada = aMilisegundos(reserva.updatedAt) || creada;
  const secuencia = Math.max(0, Math.floor((actualizada - creada) / 1000));

  return {
    uid: reservaId,
    secuencia,
    titulo,
    descripcion: lineas.join('\n'),
    lugar: cfg.address ?? '',
    inicio,
    fin,
    url,
    avisoMinutos:
      typeof cfg.reminderMinutes === 'number' && cfg.reminderMinutes > 0
        ? cfg.reminderMinutes
        : undefined,
    cancelado,
  };
};

// ── Enlaces ──────────────────────────────────────────────────────────────

/** `20260912T130000Z`: la forma que entienden Google y Yahoo. */
const compactaUtc = (d: Date): string => d.toISOString().replace(/[-:]|\.\d{3}/g, '');

const parametros = (p: Record<string, string | undefined>): string =>
  Object.entries(p)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join('&');

export const enlaceGoogle = (e: EventoCalendario): string =>
  'https://calendar.google.com/calendar/render?' +
  parametros({
    action: 'TEMPLATE',
    text: e.titulo,
    dates: `${compactaUtc(e.inicio)}/${compactaUtc(e.fin)}`,
    details: e.descripcion,
    location: e.lugar || undefined,
  });

/** Outlook personal (`live`) y Outlook de empresa (`office`) son dos sitios. */
export const enlaceOutlook = (e: EventoCalendario, cual: 'live' | 'office'): string =>
  `https://outlook.${cual}.com/calendar/0/deeplink/compose?` +
  parametros({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: e.titulo,
    startdt: e.inicio.toISOString(),
    enddt: e.fin.toISOString(),
    body: e.descripcion,
    location: e.lugar || undefined,
  });

export const enlaceYahoo = (e: EventoCalendario): string =>
  'https://calendar.yahoo.com/?' +
  parametros({
    v: '60',
    title: e.titulo,
    st: compactaUtc(e.inicio),
    et: compactaUtc(e.fin),
    desc: e.descripcion,
    in_loc: e.lugar || undefined,
  });

// ── Archivo .ics (RFC 5545) ──────────────────────────────────────────────

/** Los caracteres que el formato obliga a escapar en los textos. */
const escapar = (texto: string): string =>
  texto.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/**
 * Ninguna línea puede pasar de 75 octetos; las largas se parten y la
 * continuación empieza con un espacio. Se cuenta en bytes, no en letras: una
 * tilde ocupa dos.
 */
const plegar = (linea: string): string => {
  const bytes = new TextEncoder().encode(linea);
  if (bytes.length <= 75) return linea;
  const trozos: string[] = [];
  let actual = '';
  let peso = 0;
  for (const caracter of linea) {
    const p = new TextEncoder().encode(caracter).length;
    const limite = trozos.length === 0 ? 75 : 74;
    if (peso + p > limite) {
      trozos.push(actual);
      actual = '';
      peso = 0;
    }
    actual += caracter;
    peso += p;
  }
  if (actual) trozos.push(actual);
  return trozos.map((t, i) => (i === 0 ? t : ` ${t}`)).join('\r\n');
};

const veventDe = (e: EventoCalendario, sello: string): string[] => {
  const lineas = [
    'BEGIN:VEVENT',
    `UID:${e.uid}@sontickets.com`,
    `DTSTAMP:${sello}`,
    `SEQUENCE:${e.secuencia}`,
    `DTSTART:${compactaUtc(e.inicio)}`,
    `DTEND:${compactaUtc(e.fin)}`,
    `SUMMARY:${escapar(e.titulo)}`,
    `DESCRIPTION:${escapar(e.descripcion)}`,
    e.lugar ? `LOCATION:${escapar(e.lugar)}` : null,
    `URL:${e.url}`,
    `STATUS:${e.cancelado ? 'CANCELLED' : 'CONFIRMED'}`,
    'TRANSP:OPAQUE',
  ].filter((l): l is string => l !== null);

  if (e.avisoMinutos && !e.cancelado) {
    lineas.push(
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapar(e.titulo)}`,
      `TRIGGER:-PT${e.avisoMinutos}M`,
      'END:VALARM'
    );
  }
  lineas.push('END:VEVENT');
  return lineas;
};

/**
 * Un archivo con uno o varios eventos.
 *
 * Con varios (una reserva que se repite) el cliente los añade todos de una
 * vez, cosa que los enlaces de Google y Outlook no pueden hacer. Si todos
 * están cancelados, el archivo va con `METHOD:CANCEL` para que el calendario
 * los quite.
 */
export const archivoIcs = (eventos: EventoCalendario[]): string => {
  const sello = compactaUtc(new Date());
  const todosCancelados = eventos.length > 0 && eventos.every((e) => e.cancelado);
  const lineas = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Sontickets//Reservas//ES',
    'CALSCALE:GREGORIAN',
    `METHOD:${todosCancelados ? 'CANCEL' : 'PUBLISH'}`,
    ...eventos.flatMap((e) => veventDe(e, sello)),
    'END:VCALENDAR',
  ];
  return lineas.map(plegar).join('\r\n') + '\r\n';
};

/** Nombre del archivo: `reserva-ABCD.ics`, o `reservas-ABCD.ics` si son varias. */
export const nombreArchivoIcs = (eventos: EventoCalendario[], codigo?: string): string => {
  const base = eventos.length > 1 ? 'reservas' : 'reserva';
  const sufijo = (codigo ?? '').replace(/[^A-Za-z0-9]/g, '');
  return `${base}${sufijo ? `-${sufijo}` : ''}.ics`;
};

/**
 * Entrega el `.ics` al navegador.
 *
 * Un enlace con `download` es lo que hacen todas las librerías del ramo. Se
 * añade `target=_blank` para que, si el navegador no atiende al `download`
 * (pasa dentro de algunos iframes), al menos lo abra en una pestaña nueva, de
 * donde el sistema lo manda al calendario. No se usa `data:`: Chrome bloquea
 * la navegación a ese esquema.
 */
export const descargarIcs = (nombre: string, contenido: string): void => {
  const blob = new Blob([contenido], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.target = '_blank';
  enlace.rel = 'noopener';
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Fecha legible para la ventana: "vie 13 sep, 08:00–10:00". */
export const resumenFecha = (e: EventoCalendario, zona = ZONA_POR_DEFECTO): string => {
  const i = utcToZonedTime(e.inicio, zona);
  const f = utcToZonedTime(e.fin, zona);
  const mismoDia = format(i, 'yyyy-MM-dd') === format(f, 'yyyy-MM-dd');
  return mismoDia
    ? `${format(i, 'HH:mm')}–${format(f, 'HH:mm')}`
    : `${format(i, 'HH:mm')} → ${format(f, 'dd/MM HH:mm')}`;
};
